import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { DUE_SOON_DAYS, daysUntil, dueIn, ownerNow } from "../../src/waits.ts";
import { api, type Item, type TripView } from "./api.ts";
import { localToday, personName, sentence } from "./format.ts";
import { membersNow } from "./former.tsx";
import { haptic } from "./haptics.ts";
import { litProps, useLastOpened } from "./opened.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Button, Icon } from "./souvenir.ts";
import { factLine, Title } from "./ui.tsx";

// To-dos on the web (features/tasks-and-reminders.md), the one part of the shared plan changed here:
// anyone in the trip ticks one done (a tick on its row; again makes it not done), and on its card gives
// it to someone or nobody, moves its due date, marks it important or removes it. Each is one change of
// mine "on the web", in Updates; removing one offers Undo at once.

// A to-do just removed, for the Undo offered at once.
export interface Removed {
  id: string;
  title: string;
  change: string;
}

type Edit = { done: boolean } | { owner: string | null } | { due: string | null } | { important: boolean };
// What an edit changes, as the to-do will be.
type Shown = { status: Item["status"]; owner: string | null; due: string | null; important: boolean };

const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
// "20 Nov": a due date as people say it.
export const dueDay = (date: string) => DAY.format(new Date(`${date}T00:00:00Z`));

// Open ones first, then what's done; among each, important ones first, then the soonest due.
export function todoOrder(a: Item, b: Item): number {
  return (
    Number(a.status === "done") - Number(b.status === "done") ||
    Number(Boolean(b.important)) - Number(Boolean(a.important)) ||
    (a.due ?? "9999").localeCompare(b.due ?? "9999")
  );
}

// What I changed on a to-do that the trip as loaded doesn't show yet: its fields as they'll be (none:
// removed), how many of my edits to it are still on their way, and the version that has them all.
interface Pending {
  patch: Partial<Shown> | null;
  inflight: number;
  until: number;
}

export interface TodoEdits {
  save(item: Item, edit: Edit, patch: Partial<Shown>): Promise<void>;
  remove(item: Item): Promise<Removed>;
  // Items just taken out of the trip another way (Remove from trip, RemoveFromTrip.tsx): gone from every list at once,
  // as a to-do removed is.
  gone(ids: readonly string[]): void;
}

const Edits = createContext<TodoEdits | null>(null);
export const TodoEditsProvider = Edits.Provider;
// Hiding what was just removed, where the trip's items are (none on a page without them: nothing to hide).
export const useGone = (): ((ids: readonly string[]) => void) => useContext(Edits)?.gone ?? (() => {});

// A load shows what I changed once it has the version my last edit made (or the to-do is gone);
// until then, every list and card shows it as I left it, so going back to Bookings right after
// ticking or removing one never shows it as it was.
const pendingShows = (version: number | undefined, p: Pending) =>
  version !== undefined && (p.inflight > 0 || version < p.until);

function patched(item: Item, patch: Partial<Shown>): Item {
  const next: Item = { ...item };
  if (patch.status !== undefined) next.status = patch.status;
  if (patch.owner !== undefined) {
    if (patch.owner) next.owner = patch.owner;
    else delete next.owner;
  }
  if (patch.due !== undefined) {
    if (patch.due) next.due = patch.due;
    else delete next.due;
  }
  if (patch.important !== undefined) {
    if (patch.important) next.important = true;
    else delete next.important;
  }
  return next;
}

// The trip's items with my to-do edits on them, and the edits: one write each, one after another
// (two quick taps never cross), the trip loaded again after each.
export function useTodoEdits(trip: string, loaded: Item[], onChanged: () => void): { items: Item[]; edits: TodoEdits } {
  const [pending, setPending] = useState<ReadonlyMap<string, Pending>>(new Map());
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  // What the loads have caught up with goes.
  useEffect(() => {
    const versions = new Map(loaded.map((i) => [i.id, i.version]));
    setPending((m) => {
      const kept = [...m].filter(([id, p]) => pendingShows(versions.get(id), p));
      return kept.length === m.size ? m : new Map(kept);
    });
  }, [loaded]);
  const update = (id: string, f: (p: Pending | undefined) => Pending | null) =>
    setPending((m) => {
      const next = new Map(m);
      const p = f(m.get(id));
      if (p) next.set(id, p);
      else next.delete(id);
      return next;
    });
  const items =
    pending.size === 0
      ? loaded
      : loaded.flatMap((i) => {
          const p = pending.get(i.id);
          if (!p || !pendingShows(i.version, p)) return [i];
          return p.patch === null ? [] : [patched(i, p.patch)];
        });
  const edits: TodoEdits = {
    save(item, edit, patch) {
      update(item.id, (p) => ({
        patch: { ...p?.patch, ...patch },
        inflight: (p?.inflight ?? 0) + 1,
        until: p?.until ?? 0,
      }));
      const run = queue.current.then(async () => {
        try {
          const { version } = await api.editTodo(trip, item.id, edit);
          update(item.id, (p) => (p ? { ...p, inflight: p.inflight - 1, until: Math.max(p.until, version) } : null));
          onChanged();
        } catch (e) {
          // Not saved: the to-do as loaded, and why.
          update(item.id, () => null);
          throw e;
        }
      });
      queue.current = run.catch(() => {});
      return run;
    },
    async remove(item) {
      const run = queue.current.then(() => api.removeTodo(trip, item.id));
      queue.current = run.catch(() => {});
      const { change } = await run;
      // Gone from every list at once, until a load has it gone, or back (an undo).
      update(item.id, () => ({ patch: null, inflight: 0, until: item.version + 1 }));
      onChanged();
      return { id: item.id, title: item.title, change };
    },
    gone(ids) {
      for (const id of ids) {
        const at = loaded.find((i) => i.id === id);
        if (at) update(id, () => ({ patch: null, inflight: 0, until: at.version + 1 }));
      }
    },
  };
  return { items, edits };
}

// A to-do as it is, with my edits on it, and making them.
const shownOf = (item: Item): Shown => ({
  status: item.status,
  owner: item.owner ?? null,
  due: item.due ?? null,
  important: Boolean(item.important),
});

function useTodo(item: Item) {
  const edits = useContext(Edits);
  if (!edits) throw new Error("to-dos are edited within a trip");
  const [error, setError] = useState<string | null>(null);
  const shown = shownOf(item);
  const save = (edit: Edit, patch: Partial<Shown>) => {
    setError(null);
    edits.save(item, edit, patch).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : "Couldn't change the to-do. Try again.");
    });
  };
  // Ticked done on a phone, a tick under the finger; not when it's unticked.
  const toggleDone = (e: { nativeEvent: object }) => {
    const done = shown.status !== "done";
    if (done) haptic("commit", e);
    save({ done }, { status: done ? "done" : "todo" });
  };
  return { shown, error, setError, save, toggleDone, remove: () => edits.remove(item) };
}

// The tick: a circle, filled and checked once done. Its touch target is larger than it looks.
function Tick({
  done,
  label,
  onToggle,
}: {
  done: boolean;
  label: string;
  onToggle: (e: { nativeEvent: object }) => void;
}) {
  const icon = (
    <span className={`tm-row-icon tm-task-icon${done ? " is-done" : ""}`} aria-hidden="true">
      {done && <Icon name="check" size={18} />}
    </span>
  );
  // A read-only trip: whether it's done, to see, not to tick (the row's line says it in words).
  if (useReadOnly()) return <span className="tm-todo-tick is-still">{icon}</span>;
  return (
    <button type="button" className="tm-todo-tick" aria-pressed={done} aria-label={label} onClick={onToggle}>
      {icon}
    </button>
  );
}

// A flag, drawn like Souvenir's icons (it has none): what marks an important to-do.
export function Flag({ size = 14 }: { size?: number }) {
  return (
    <svg
      className="tm-flag"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 21V4" />
      <path d="M5 4h11l-2 4.5 2 4.5H5" fill="currentColor" fillOpacity="0.25" />
    </svg>
  );
}

// "Important", in coral with its flag: first on a to-do's line.
export function ImportantMark() {
  return (
    <span className="tm-important">
      <Flag />
      Important
    </span>
  );
}

// How far a due date is ("in 5 days", "tomorrow", "today", "2 days late"), wherever one shows: coral
// once it's late, the sun's colour within 7 days, quiet after that (DESIGN.md, Views, "What waits on me
// leads").
export function DueIn({ due, today = localToday() }: { due: string; today?: string }) {
  const left = daysUntil(due, today);
  const tone = left < 0 ? " is-late" : left <= DUE_SOON_DAYS ? " is-soon" : "";
  return <span className={`tm-due${tone}`}>{dueIn(due, today)}</span>;
}

// Whose a to-do is now: its owner while they're in the trip, else nobody's (src/waits.ts, `ownerNow`), as
// Bookings' Waiting for you, the tab and the reminders count it.
const whose = (shown: Pick<Shown, "owner">, trip: TripView) =>
  ownerNow({ owner: shown.owner ?? undefined }, membersNow(trip));

// A to-do as a line says it: whether it's done, whose, by when and whether it's important.
type TodoShape = {
  status: string;
  owner?: string | null | undefined;
  due?: string | null | undefined;
  important?: boolean | undefined;
};

// Whose a to-do is, as a fact on a line: "yours", "nobody's yet", or a name, which stays a name. Only first on
// its line does it start with a capital ("Yours · by 25 Oct"); after a state or the important mark it doesn't
// ("To-do · yours", "Important · nobody's yet": DESIGN.md, Bookings, and features/on-the-road.md, Today's rows).
// A done to-do that was nobody's says nothing of whose: "Done", never "Done · nobody's yet", which reads as still
// waiting on someone.
function whoseFact(task: TodoShape, trip: TripView, first: boolean): string | null {
  const owner = personName(whose({ owner: task.owner ?? null }, trip), trip.members, trip.you);
  if (!owner && task.status === "done") return null;
  const said = owner ? (owner === "You" ? "yours" : owner) : "nobody's yet";
  return first ? sentence(said) : said;
}

// A to-do's facts after its state, the same wherever a to-do is said (DESIGN.md, Bookings: "To-do · nobody's
// yet · by 25 Oct · in 2 days", and a booking's to-do "To book · Important · nobody's yet · by 25 Oct · in 2
// days · booking opens 25 Oct"): whether it's important, whose, and while it's open its due date with how far
// that is right after it.
export function todoFacts(task: TodoShape, trip: TripView, today?: string): ReactNode[] {
  const open = task.status !== "done";
  return [
    task.important ? <ImportantMark key="important" /> : null,
    whoseFact(task, trip, false),
    task.due && open ? `by ${dueDay(task.due)}` : null,
    task.due && open ? <DueIn key="due" due={task.due} {...(today ? { today } : {})} /> : null,
  ];
}

// What a to-do's line says after its title. `state` (Bookings, where each row's state leads its line: DESIGN.md,
// Bookings): "To-do" or "Done" first, then its facts ("To-do · Important · nobody's yet · by 25 Oct · in 2
// days"). `calm` (Today): no date and nothing late, only that it's a to-do and whose (never rigid: DESIGN.md,
// "Today, during the trip"), an important one's mark first ("To-do · yours", "Important · To-do · Mei").
// Elsewhere (a card's to-dos), an important one's mark first, whether it's done, whose, by when ("Yours · by 25
// Oct · in 2 days", "Done · Ben").
function facts(shown: Shown, trip: TripView, calm: boolean, state: boolean): ReactNode {
  const done = shown.status === "done";
  if (state) return factLine([done ? "Done" : "To-do", ...todoFacts(shown, trip)]);
  const lead = [
    shown.important ? <ImportantMark key="important" /> : null,
    done ? "Done" : calm ? "To-do" : null,
  ].filter(Boolean);
  const parts: ReactNode[] = [...lead, whoseFact(shown, trip, lead.length === 0)];
  if (shown.due && !done && !calm) parts.push(`by ${dueDay(shown.due)}`, <DueIn key="due" due={shown.due} />);
  return factLine(parts);
}

// A to-do in a list: the tick, and the rest of the row opens its card. Long titles wrap.
export function TodoRow({
  item,
  trip,
  onOpen,
  calm = false,
  state = false,
}: {
  item: Item;
  trip: TripView;
  onOpen: (id: string) => void;
  calm?: boolean;
  // Its state first on its line, as every row on Bookings has.
  state?: boolean;
}) {
  const { shown, error, toggleDone } = useTodo(item);
  const done = shown.status === "done";
  // The last thing opened from the view it's in, lit.
  const lit = useLastOpened() === item.id;
  return (
    <li>
      <div className={`tm-todo-row${done ? " is-done" : ""}${lit ? " is-lit" : ""}`}>
        <Tick done={done} label={`Done: ${item.title}`} onToggle={toggleDone} />
        <button type="button" className="tm-todo-open" {...litProps(lit)} onClick={() => onOpen(item.id)}>
          <span className="tm-row-text">
            <span className="callout">
              <Title text={item.title} />
            </span>
            <span className="caption tm-muted">{facts(shown, trip, calm, state)}</span>
          </span>
        </button>
      </div>
      {error && (
        <p className="caption tm-error tm-todo-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </li>
  );
}

// On a to-do's card: done, who it's for, when it's due, whether it's important, and removing it. On a
// read-only trip, the same said in a line, to read ("To-do · Important · yours · by 25 Oct · in 2 days").
export function TodoEdit(props: { item: Item; trip: TripView; onRemoved: (removed: Removed) => void }) {
  if (useReadOnly())
    return (
      <section className="tm-todo-edit" aria-label="This to-do">
        <p className="callout">{facts(shownOf(props.item), props.trip, false, true)}</p>
      </section>
    );
  return <TodoFields {...props} />;
}

function TodoFields({ item, trip, onRemoved }: { item: Item; trip: TripView; onRemoved: (removed: Removed) => void }) {
  const { shown, error, setError, save: saveIt, toggleDone: toggleIt, remove: removeIt } = useTodo(item);
  const done = shown.status === "done";
  // Where a change that didn't go through is said: beside what made it (Done, the fields, Important, Remove), not
  // at the card's foot, out of sight of a Done tapped with no signal.
  const [failedAt, setFailedAt] = useState<"done" | "fields" | "important" | "remove">("done");
  const save = (
    at: "fields" | "important",
    edit: Parameters<typeof saveIt>[0],
    patch: Parameters<typeof saveIt>[1],
  ) => {
    setFailedAt(at);
    saveIt(edit, patch);
  };
  const toggleDone = (e: { nativeEvent: object }) => {
    setFailedAt("done");
    toggleIt(e);
  };
  const failed = (at: typeof failedAt) =>
    error && failedAt === at ? (
      <p className="caption tm-error tm-todo-failed" role="alert">
        {error}
        <ReportThis />
      </p>
    ) : null;
  // The due date as I type or pick it; saved once I'm done with it (it leaves the field, or Enter).
  const [due, setDue] = useState(item.due ?? "");
  useEffect(() => setDue(item.due ?? ""), [item.due]);
  const [removing, setRemoving] = useState(false);
  const commitDue = () => {
    const next = due || null;
    if (next === shown.due) return;
    // Only a whole date: a half-typed one goes back to what it was.
    if (next && !/^\d{4}-\d{2}-\d{2}$/.test(next)) return setDue(shown.due ?? "");
    save("fields", { due: next }, { due: next });
  };
  const remove = async () => {
    setFailedAt("remove");
    setRemoving(true);
    setError(null);
    try {
      onRemoved(await removeIt());
    } catch (e) {
      setRemoving(false);
      setError(e instanceof Error ? e.message : "Couldn't remove the to-do. Try again.");
    }
  };
  // Others first by name, me first of all.
  const people = [...trip.members].sort(
    (a, b) => Number(b.id === trip.you) - Number(a.id === trip.you) || a.name.localeCompare(b.name),
  );
  return (
    <section className="tm-todo-edit" aria-label="This to-do">
      <button
        type="button"
        className={`tm-todo-done${done ? " is-done" : ""}`}
        data-edit="todo-done"
        aria-pressed={done}
        onClick={toggleDone}
      >
        <span className={`tm-row-icon tm-task-icon${done ? " is-done" : ""}`} aria-hidden="true">
          {done && <Icon name="check" size={18} />}
        </span>
        <span className="callout">Done</span>
      </button>
      {failed("done")}
      <div className="tm-todo-fields">
        <label className="tm-todo-field" htmlFor={`todo-owner-${item.id}`}>
          <span className="callout tm-muted">For</span>
        </label>
        <span className="tm-todo-select">
          <select
            id={`todo-owner-${item.id}`}
            data-edit="todo-owner"
            value={whose(shown, trip) ?? ""}
            onChange={(e) => {
              const owner = e.target.value || null;
              save("fields", { owner }, { owner });
            }}
          >
            <option value="">Nobody yet</option>
            {people.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id === trip.you ? `You (${m.name})` : m.name}
              </option>
            ))}
          </select>
          <span className="tm-todo-chevron" aria-hidden="true">
            <Icon name="chevron" size={16} />
          </span>
        </span>
        <label className="tm-todo-field" htmlFor={`todo-due-${item.id}`}>
          <span className="callout tm-muted">Due</span>
        </label>
        <span className="tm-todo-due">
          <input
            id={`todo-due-${item.id}`}
            data-edit="todo-due"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            onBlur={commitDue}
            onKeyDown={(e) => e.key === "Enter" && commitDue()}
          />
          {shown.due && (
            <span className="tm-todo-due-after">
              {!done && <DueIn due={shown.due} />}
              <button
                type="button"
                className="tm-todo-clear"
                onClick={() => {
                  setDue("");
                  save("fields", { due: null }, { due: null });
                }}
              >
                No date
              </button>
            </span>
          )}
        </span>
      </div>
      {failed("fields")}
      <label className="tm-todo-important">
        <input
          type="checkbox"
          data-edit="todo-important"
          checked={shown.important}
          onChange={(e) => {
            const important = e.target.checked;
            save("important", { important }, { important });
          }}
        />
        <span className="tm-todo-important-text">
          <span className="callout">
            Important <Flag />
          </span>
          <span className="caption tm-muted">
            Everyone in the trip hears when it's done, moved, given to someone or removed.
          </span>
        </span>
      </label>
      {failed("important")}
      <Button
        variant="quiet"
        size="sm"
        icon="close"
        className="tm-start"
        data-edit="todo-remove"
        disabled={removing}
        onClick={remove}
      >
        {removing ? "Removing…" : "Remove to-do"}
      </Button>
      {failed("remove")}
    </section>
  );
}

// Right after something I did on the web (a to-do removed, a booking saved): what it was, and Undo, for a
// while (Updates keeps it after that); with no change to undo (only a file attached), what was done, and OK.
export function UndoNotice({
  trip,
  change,
  text,
  onUndone,
  onGone,
}: {
  trip: string;
  change: string | null;
  text: string;
  onUndone: () => void;
  onGone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gone = useRef(onGone);
  gone.current = onGone;
  // biome-ignore lint/correctness/useExhaustiveDependencies: each one gets its own time, from when it shows
  useEffect(() => {
    if (error) return;
    const timer = setTimeout(() => gone.current(), 10_000);
    return () => clearTimeout(timer);
  }, [change, text, error]);
  const undo = async () => {
    if (!change) return;
    setBusy(true);
    try {
      await api.undo(trip, change);
      onUndone();
      gone.current();
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : "Couldn't undo. Try again from Updates.");
    }
  };
  return (
    <div className="tm-live tm-live-passing tm-undo" role="status">
      <span className="callout">{error ? "Couldn't undo: try again from Updates." : text}</span>
      {error || !change ? (
        <Button variant="primary" size="sm" onClick={() => gone.current()}>
          OK
        </Button>
      ) : (
        <Button variant="primary" size="sm" icon="replay" disabled={busy} onClick={undo}>
          {busy ? "Undoing…" : "Undo"}
        </Button>
      )}
    </div>
  );
}
